/**
 * Examen blanc en cours, conservé dans le navigateur.
 *
 * La page d'examen gardait tout en mémoire React : un F5, un onglet fermé par erreur ou un
 * plantage ramenaient l'étudiant à « Commencer l'examen » avec 50 minutes neuves et 0 réponse.
 * On conserve ici, par utilisateur, l'examen choisi, l'échéance du chrono (le temps continue de
 * courir pendant la coupure), les réponses et la question courante ; la page reprend là où
 * l'étudiant en était, et efface l'entrée dès que la copie est rendue. `localStorage` plutôt que
 * `sessionStorage` pour survivre à la fermeture de l'onglet ; chaque accès est protégé, le
 * stockage peut être indisponible (navigation privée, données de site bloquées).
 */
import type { CertQuestionAnswer } from '@unity-learning/shared';
import { secondsUntil } from './examTimer';

export interface ExamInProgress {
    examId: string;
    /** ISO, envoyé tel quel au serveur à la soumission. */
    startedAt: string;
    /** Échéance du chrono en millisecondes (Date.now()). */
    deadline: number;
    answers: Record<string, CertQuestionAnswer>;
    currentIndex: number;
}

export function examInProgressKey(userId: string): string {
    return `prog2d.examEnCours.${userId}`;
}

/** Stockage minimal attendu (celui de `localStorage`), injectable dans les tests. */
export type ProgressStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): ProgressStorage | null {
    try {
        return typeof localStorage === 'undefined' ? null : localStorage;
    } catch {
        return null;
    }
}

export function readExamInProgress(userId: string, storage: ProgressStorage | null = defaultStorage()): ExamInProgress | null {
    try {
        const raw = storage?.getItem(examInProgressKey(userId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<ExamInProgress>;
        if (typeof parsed.examId !== 'string' || typeof parsed.startedAt !== 'string'
            || typeof parsed.deadline !== 'number' || !parsed.answers || typeof parsed.answers !== 'object') {
            return null;
        }
        return {
            examId: parsed.examId,
            startedAt: parsed.startedAt,
            deadline: parsed.deadline,
            answers: parsed.answers as Record<string, CertQuestionAnswer>,
            currentIndex: typeof parsed.currentIndex === 'number' ? parsed.currentIndex : 0,
        };
    } catch {
        return null;
    }
}

export function writeExamInProgress(userId: string, state: ExamInProgress, storage: ProgressStorage | null = defaultStorage()): void {
    try {
        storage?.setItem(examInProgressKey(userId), JSON.stringify(state));
    } catch {
        /* stockage indisponible : l'examen continue sans reprise possible */
    }
}

export function clearExamInProgress(userId: string, storage: ProgressStorage | null = defaultStorage()): void {
    try {
        storage?.removeItem(examInProgressKey(userId));
    } catch {
        /* rien à faire */
    }
}

/**
 * Réponses à reprendre : celles de l'examen fraîchement chargé (une entrée par question), où les
 * réponses sauvegardées remplacent les vides. Une réponse sauvegardée pour une question qui
 * n'existe plus est ignorée.
 */
export function mergeSavedAnswers(
    initial: Record<string, CertQuestionAnswer>,
    saved: Record<string, CertQuestionAnswer>,
): Record<string, CertQuestionAnswer> {
    const merged: Record<string, CertQuestionAnswer> = { ...initial };
    for (const [questionId, answer] of Object.entries(saved)) {
        if (questionId in merged && answer && typeof answer === 'object') {
            merged[questionId] = { ...merged[questionId], ...answer, questionId };
        }
    }
    return merged;
}

/**
 * Vrai quand l'échéance d'un examen gardé dans le navigateur est passée. Le reprendre l'enverrait
 * aussitôt, avec les réponses laissées : la page demande alors à l'élève de choisir (reprendre ou
 * abandonner) au lieu de rendre la copie à sa place.
 */
export function isExamTimeUp(saved: Pick<ExamInProgress, 'deadline'>, nowMs: number): boolean {
    return secondsUntil(saved.deadline, nowMs) === 0;
}

/** Nombre de questions auxquelles l'examen gardé a une réponse. */
export function countSavedAnswers(saved: Pick<ExamInProgress, 'answers'>): number {
    return Object.values(saved.answers).filter(a => a && typeof a === 'object' && a.isAnswered === true).length;
}

/** Index de reprise borné au nombre de questions. */
export function clampIndex(index: number, questionCount: number): number {
    if (!Number.isFinite(index) || index < 0) return 0;
    return Math.min(Math.floor(index), Math.max(0, questionCount - 1));
}

/**
 * Session d'Entraînement IA interrompue. Les questions et les réponses vivent côté serveur
 * (en mémoire, par session) : il suffit de retenir l'id de la session, l'échéance et la
 * question courante ; la page recharge le reste via GET /api/ai-training/session/:id.
 */
export interface AiTrainingInProgress {
    sessionId: string;
    deadline: number;
    currentIndex: number;
}

export function aiTrainingInProgressKey(userId: string): string {
    return `prog2d.entrainementIAEnCours.${userId}`;
}

export function readAiTrainingInProgress(userId: string, storage: ProgressStorage | null = defaultStorage()): AiTrainingInProgress | null {
    try {
        const raw = storage?.getItem(aiTrainingInProgressKey(userId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<AiTrainingInProgress>;
        if (typeof parsed.sessionId !== 'string' || typeof parsed.deadline !== 'number') return null;
        return {
            sessionId: parsed.sessionId,
            deadline: parsed.deadline,
            currentIndex: typeof parsed.currentIndex === 'number' ? parsed.currentIndex : 0,
        };
    } catch {
        return null;
    }
}

export function writeAiTrainingInProgress(userId: string, state: AiTrainingInProgress, storage: ProgressStorage | null = defaultStorage()): void {
    try {
        storage?.setItem(aiTrainingInProgressKey(userId), JSON.stringify(state));
    } catch {
        /* stockage indisponible */
    }
}

export function clearAiTrainingInProgress(userId: string, storage: ProgressStorage | null = defaultStorage()): void {
    try {
        storage?.removeItem(aiTrainingInProgressKey(userId));
    } catch {
        /* rien à faire */
    }
}
